import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import { type SanityCompareView } from "./compare.type";
import { cellToIconValue } from "./compareTableCell";
import { CompareTableIcon } from "./compareTableIcon.component";

export const CompareTableGroup = ({
  columns,
  rows,
  showFade,
}: CompareTableGroupProps) => {
  if (!columns?.length || !rows?.length) return null;

  const renderRows = (colIndex: number) => {
    return rows.map((row) => (
      <div
        role="cell"
        key={row.id}
        className="border-greyscale-strength flex h-[100px] items-center justify-center border-b px-2 py-4">
        <CompareTableIcon value={cellToIconValue(row.cells?.[colIndex])} />
      </div>
    ));
  };

  return (
    <>
      {columns.map((column, colIndex) => {
        const ctaHref = appendQueryString(
          column.ctaLink?.url,
          column.ctaLink?.queryString,
        );
        return (
          <div
            role="row"
            key={column.id}
            className={cn("group", {
              "opacity-50": showFade && colIndex > 3,
            })}>
            <div
              role="columnheader"
              className="border-greyscale-strength flex h-[250px] flex-col items-center justify-center border-b">
              <Image
                src={column.imageUrl as string}
                alt={column.alt?.trim() || ""}
                width={183}
                height={183}
              />
              <Text
                variant="h6"
                component="div"
                className="text-center"
                role="cell">
                {column.title}
              </Text>
            </div>

            {renderRows(colIndex)}

            <div
              className="border-greyscale-strength flex items-center justify-center py-5"
              role="cell">
              {ctaHref && (
                <Link
                  href={ctaHref}
                  secondary
                  className="transition-opacity duration-150 group-hover:opacity-100 md:opacity-0"
                  target={column.ctaLink?.openInNewTab ? "_blank" : undefined}
                  rel={
                    column.ctaLink?.openInNewTab
                      ? "noopener noreferrer"
                      : undefined
                  }>
                  {column.ctaLabel?.trim() || "Customize now"}
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

type CompareTableGroupProps = {
  columns: SanityCompareView["columns"];
  rows: SanityCompareView["rows"];
  showFade?: boolean;
};
